import { IRecurringProductRepository } from './interfaces/IRecurringProductRepository';
import { RecurringProductDB, RecurringProductFormData, RecurringProductUpdateData } from '@/types/recurring-unified';
import { offlineDb, SyncableRecurringProduct } from '@/db/offlineDb';
import OfflineLogger from '@/utils/offlineLogger';

const STORE_NAME = 'recurring_products';

type RecurringProductRecord = SyncableRecurringProduct;

class RecurringProductRepositoryEnhanced implements IRecurringProductRepository {
  private table = offlineDb.recurringProducts;

  async getAll(): Promise<RecurringProductDB[]> {
    try {
      const records = await this.table.toArray();
      return records
        .filter(record => record._syncStatus !== 'deleted')
        .map(this.recordToDB);
    } catch (error) {
      OfflineLogger.error('RecurringProductRepository.getAll', error);
      return [];
    }
  }

  async getById(id: string): Promise<RecurringProductDB | null> {
    try {
      const record = await this.table.get(id);
      if (!record || record._syncStatus === 'deleted') return null;
      return this.recordToDB(record);
    } catch (error) {
      OfflineLogger.error('RecurringProductRepository.getById', error);
      return null;
    }
  }

  async getByUserId(userId: string): Promise<RecurringProductDB[]> {
    try {
      const records = await this.table.where('user_id').equals(userId).toArray();
      return records
        .filter(record => record._syncStatus !== 'deleted')
        .map(this.recordToDB);
    } catch (error) {
      OfflineLogger.error('RecurringProductRepository.getByUserId', error);
      return [];
    }
  }

  async create(
    data: RecurringProductFormData & { user_id: string; id?: string; updated_at?: string; created_at?: string },
    syncStatus: 'pending' | 'synced' = 'pending'
  ): Promise<RecurringProductDB> {
    try {
      const now = new Date().toISOString();

      const record: RecurringProductRecord = {
        id: data.id || crypto.randomUUID(),
        user_id: data.user_id,
        product_id: data.productId,
        frequency_days: data.frequencyDays,
        recurrence_group: data.recurrenceGroup,
        last_purchase_date: (data as any).last_purchase_date,
        next_purchase_date: data.nextPurchaseDate,
        active: true,
        created_at: data.created_at || now,
        updated_at: data.updated_at || now,
        attributes: data.attributes,
        _lastModified:
          syncStatus === 'pending'
            ? Date.now()
            : new Date(data.updated_at || now).getTime(),
        _syncStatus: syncStatus,
        // Store denormalized data if available
        product_name: (data as any).product_name,
        product_default_quantity: (data as any).product_default_quantity,
        product_default_unit: (data as any).product_default_unit,
        category_name: (data as any).category_name,
        category_icon: (data as any).category_icon
      };

      const existing = await this.table.get(record.id);
      if (existing) {
        // Preserve local pending changes
        if (existing._syncStatus === 'pending' || existing._lastModified > record._lastModified) {
          OfflineLogger.info(
            'RecurringProductRepository.create',
            `Skipped creating product ${record.id} due to newer local version`
          );
          return this.recordToDB(existing);
        }
      }

      await this.table.put({ ...existing, ...record });
      OfflineLogger.info('RecurringProductRepository.create', `Created product ${record.id}`);

      return this.recordToDB({ ...existing, ...record });
    } catch (error) {
      OfflineLogger.error('RecurringProductRepository.create', error);
      throw error;
    }
  }

  async update(id: string, updates: RecurringProductUpdateData | any): Promise<RecurringProductDB | null> {
    try {
      const existing = await this.table.get(id);
      
      if (!existing || existing._syncStatus === 'deleted') {
        return null;
      }

      // Map form format updates to DB format if needed
      const dbUpdates: any = {};
      
      if (updates.frequencyDays !== undefined) {
        dbUpdates.frequency_days = updates.frequencyDays;
      }
      if (updates.frequency_days !== undefined) {
        dbUpdates.frequency_days = updates.frequency_days;
      }
      if (updates.recurrenceGroup !== undefined) {
        dbUpdates.recurrence_group = updates.recurrenceGroup;
      }
      if (updates.recurrence_group !== undefined) {
        dbUpdates.recurrence_group = updates.recurrence_group;
      }
      if (updates.nextPurchaseDate !== undefined) {
        dbUpdates.next_purchase_date = updates.nextPurchaseDate;
      }
      if (updates.next_purchase_date !== undefined) {
        dbUpdates.next_purchase_date = updates.next_purchase_date;
      }
      if (updates.attributes !== undefined) {
        dbUpdates.attributes = updates.attributes;
      }
      if (updates.status !== undefined) {
        dbUpdates.active = updates.status === 'active';
      }
      if ((updates as any).productId !== undefined) {
        dbUpdates.product_id = (updates as any).productId;
      }
      if ((updates as any).product_id !== undefined) {
        dbUpdates.product_id = (updates as any).product_id;
      }
      if ((updates as any).product_name !== undefined) {
        dbUpdates.product_name = (updates as any).product_name;
      }
      if ((updates as any).product_default_quantity !== undefined) {
        dbUpdates.product_default_quantity = (updates as any).product_default_quantity;
      }
      if ((updates as any).product_default_unit !== undefined) {
        dbUpdates.product_default_unit = (updates as any).product_default_unit;
      }
      if ((updates as any).category_name !== undefined) {
        dbUpdates.category_name = (updates as any).category_name;
      }
      if ((updates as any).category_icon !== undefined) {
        dbUpdates.category_icon = (updates as any).category_icon;
      }

      const updated: RecurringProductRecord = {
        ...existing,
        ...dbUpdates,
        updated_at: new Date().toISOString(),
        _lastModified: Date.now(),
        _syncStatus: 'pending'
      };

      await this.table.put(updated);
      OfflineLogger.info('RecurringProductRepository.update', `Updated product ${id}`);

      return this.recordToDB(updated);
    } catch (error) {
      OfflineLogger.error('RecurringProductRepository.update', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      const existing = await this.table.get(id);
      
      if (!existing) return;

      const deleted: RecurringProductRecord = {
        ...existing,
        _lastModified: Date.now(),
        _syncStatus: 'deleted'
      };

      await this.table.put(deleted);
      OfflineLogger.info('RecurringProductRepository.delete', `Marked product ${id} for deletion`);
    } catch (error) {
      OfflineLogger.error('RecurringProductRepository.delete', error);
      throw error;
    }
  }

  async createMany(
    items: (RecurringProductFormData & { user_id: string; id?: string; updated_at?: string; created_at?: string })[],
    syncStatus: 'pending' | 'synced' = 'pending'
  ): Promise<RecurringProductDB[]> {
    const results: RecurringProductDB[] = [];
    for (const item of items) {
      const result = await this.create(item, syncStatus);
      results.push(result);
    }
    return results;
  }

  async updateMany(updates: Array<{ id: string; data: RecurringProductUpdateData }>): Promise<void> {
    for (const { id, data } of updates) {
      await this.update(id, data);
    }
  }

  async deleteMany(ids: string[]): Promise<void> {
    for (const id of ids) {
      await this.delete(id);
    }
  }

  async getPendingChanges(): Promise<RecurringProductDB[]> {
    try {
      const records = await this.table.where('_syncStatus').equals('pending').toArray();
      return records.map(this.recordToDB);
    } catch (error) {
      OfflineLogger.error('RecurringProductRepository.getPendingChanges', error);
      return [];
    }
  }

  async markAsSynced(id: string): Promise<void> {
    try {
      const existing = await this.table.get(id);
      if (!existing) return;

      if (existing._syncStatus === 'deleted') {
        await this.table.delete(id);
      } else {
        const synced: RecurringProductRecord = {
          ...existing,
          _syncStatus: 'synced'
        };
        await this.table.put(synced);
      }
    } catch (error) {
      OfflineLogger.error('RecurringProductRepository.markAsSynced', error);
    }
  }

  async markManyAsSynced(ids: string[]): Promise<void> {
    for (const id of ids) {
      await this.markAsSynced(id);
    }
  }

  async getActiveProducts(userId: string): Promise<RecurringProductDB[]> {
    try {
      const allRecords = await this.table.where('user_id').equals(userId).toArray();
      return allRecords
        .filter(record => record.active && record._syncStatus !== 'deleted')
        .map(this.recordToDB);
    } catch (error) {
      OfflineLogger.error('RecurringProductRepository.getActiveProducts', error);
      return [];
    }
  }

  async getOverdueProducts(userId: string): Promise<RecurringProductDB[]> {
    try {
      const products = await this.getActiveProducts(userId);
      const now = new Date();
      return products.filter(product => {
        if (!product.next_purchase_date) return false;
        return new Date(product.next_purchase_date) < now;
      });
    } catch (error) {
      OfflineLogger.error('RecurringProductRepository.getOverdueProducts', error);
      return [];
    }
  }

  async getByRecurrenceGroup(userId: string, group: string): Promise<RecurringProductDB[]> {
    try {
      const records = await this.table
        .where('recurrence_group')
        .equals(group)
        .toArray();
      return records
        .filter(record => record.user_id === userId && record._syncStatus !== 'deleted')
        .map(this.recordToDB);
    } catch (error) {
      OfflineLogger.error('RecurringProductRepository.getByRecurrenceGroup', error);
      return [];
    }
  }

  async getSyncStats(): Promise<{ total: number; synced: number; pending: number; deleted: number }> {
    try {
      const allRecords = await this.table.toArray();
      
      const stats = {
        total: allRecords.length,
        synced: allRecords.filter(r => r._syncStatus === 'synced').length,
        pending: allRecords.filter(r => r._syncStatus === 'pending').length,
        deleted: allRecords.filter(r => r._syncStatus === 'deleted').length
      };
      
      return stats;
    } catch (error) {
      OfflineLogger.error('RecurringProductRepository.getSyncStats', error);
      return { total: 0, synced: 0, pending: 0, deleted: 0 };
    }
  }

  async getProductStats(userId: string): Promise<{ total: number; active: number; overdue: number }> {
    try {
      const allProducts = await this.getByUserId(userId);
      const activeProducts = allProducts.filter(p => p.active);
      const overdueProducts = await this.getOverdueProducts(userId);
      
      return {
        total: allProducts.length,
        active: activeProducts.length,
        overdue: overdueProducts.length
      };
    } catch (error) {
      OfflineLogger.error('RecurringProductRepository.getProductStats', error);
      return { total: 0, active: 0, overdue: 0 };
    }
  }

  private recordToDB(record: RecurringProductRecord): RecurringProductDB {
    const { _lastModified, _syncStatus, ...db } = record;
    return db;
  }
}

export const recurringProductRepositoryEnhanced = new RecurringProductRepositoryEnhanced();
